import { useEffect, useState } from "react";
import {
  Settings as SettingsIcon, Check, X, ShieldCheck, ShieldAlert, Server, Cpu, CreditCard,
  Lock, LogOut, KeyRound,
} from "lucide-react";
import { getSettings } from "@/api/entities";
import { Panel, Label, Heading, Loading, Pill, HudButton, accent } from "@/components/hud";
import { useOperator } from "@/context/OperatorContext";
import WalletWatcher from "@/components/WalletWatcher";

export default function Settings() {
  const { canWrite, email, backupCodesRemaining, logout } = useOperator();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSettings().then(setS).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading label="Reading system config…" />;
  if (!s) return null;

  return (
    <div className="space-y-6 yb-rise" data-testid="settings-page">
      <div>
        <Label>Settings · system status</Label>
        <h1 className="font-display text-[26px] sm:text-[32px] mt-1.5" style={{ color: "var(--yb-text)" }}>
          System Config
        </h1>
        <p className="text-[13px] mt-2 max-w-2xl" style={{ color: "var(--yb-text-muted)" }}>
          Read-only view of how the cockpit is wired. Secrets live server-side and are never exposed to
          the browser — these are presence flags only.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel>
          <Heading title="Integrations" kicker="presence flags" />
          <ul className="space-y-3">
            <StatusRow icon={CreditCard} label="Stripe (treasury)" ok={s.stripe_configured}
              okText="Connected — recording real revenue" badText="Not connected — treasury stays $0.00" />
            <StatusRow icon={Cpu} label="LLM (agent drafting)" ok={s.llm_configured}
              okText="Connected — agents draft with AI" badText="Offline — agents use labelled templates" />
            <StatusRow icon={Server} label="Database" ok={s.db_connected !== false}
              okText="MongoDB connected" badText="Database unreachable" />
          </ul>
        </Panel>

        <Panel>
          <Heading title="Operator access" kicker="google + authenticator" />
          <ul className="space-y-3">
            <StatusRow icon={Lock} label="Signed in as" ok={!!email}
              okText={email || "—"} badText="Not signed in" />
            <StatusRow icon={canWrite ? ShieldCheck : ShieldAlert} label="Two-factor" ok={canWrite}
              okText="Authenticator app verified for this session"
              badText="Not verified for this session" />
            <StatusRow icon={KeyRound} label="Backup codes" ok={(backupCodesRemaining ?? 0) > 0}
              okText={`${backupCodesRemaining} unused code${backupCodesRemaining === 1 ? "" : "s"} left`}
              badText="None left — re-pair your authenticator to get more" />
          </ul>
          <p className="text-[11px] mt-3 leading-relaxed" style={{ color: "var(--yb-text-muted)" }}>
            Every page requires a Google sign-in on the allowlist plus a code from your
            authenticator app. The allowlist is set server-side and cannot be changed from
            the browser.
          </p>
          <HudButton variant="ghost" tone="magenta" onClick={logout} className="mt-4">
            <LogOut size={14} /> Sign out
          </HudButton>
        </Panel>
      </div>

      <WalletWatcher />

      <Panel>
        <Heading title="Runtime" kicker="environment" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <KV k="Currency" v={(s.currency || "AUD").toUpperCase()} />
          <KV k="Operator" v={s.operator_name || "BASHAM"} />
          <KV k="Region" v={s.region || "Melbourne AU"} />
          <KV k="Version" v={s.version || "v5"} />
        </div>
      </Panel>

      <Panel className="flex items-start gap-3">
        <ShieldCheck size={18} style={{ color: "var(--yb-mint)" }} className="shrink-0 mt-0.5" />
        <div>
          <div className="font-head text-[14px]" style={{ color: "var(--yb-text-dim)" }}>Safety posture</div>
          <ul className="text-[12px] mt-1.5 space-y-1" style={{ color: "var(--yb-text-muted)" }}>
            <li>• No funds can be moved from this application — there are no transfer or withdrawal controls.</li>
            <li>• No agent job runs without an explicit human approval recorded on the job.</li>
            <li>• Money displayed is only real Stripe-settled value; projections are labelled and kept separate.</li>
          </ul>
        </div>
      </Panel>
    </div>
  );
}

function StatusRow({ icon: Icon, label, ok, okText, badText }) {
  const tone = ok ? "mint" : "amber";
  return (
    <li className="flex items-start gap-3">
      <span className="w-9 h-9 rounded flex items-center justify-center shrink-0"
            style={{ border: `1px solid ${accent(tone).c}`, color: accent(tone).c }}>
        <Icon size={16} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13px]" style={{ color: "var(--yb-text)" }}>{label}</span>
          <Pill tone={tone}>{ok ? "online" : "offline"}</Pill>
        </div>
        <div className="text-[12px] mt-0.5" style={{ color: "var(--yb-text-muted)" }}>{ok ? okText : badText}</div>
      </div>
      <span style={{ color: accent(tone).c }}>{ok ? <Check size={16} /> : <X size={16} />}</span>
    </li>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--yb-text-muted)" }}>{k}</div>
      <div className="font-head text-[15px] mt-1" style={{ color: "var(--yb-text)" }}>{v}</div>
    </div>
  );
}
